import React, { useState, useEffect, useRef } from 'react';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import { url } from '../../util/config';

const LeftNavigation = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [chatbotOpen,   setChatbotOpen]   = useState(true);
    const [agentsOpen,    setAgentsOpen]    = useState(true);
    const [settingsOpen,  setSettingsOpen]  = useState(false);

    const SETTINGS_NAV = [
        { name: 'Organisation Settings', icon: 'bi-building-gear',  path: '/settings' },
        { name: 'Platform Settings',     icon: 'bi-shield-gear',    path: '/platform-settings' },
    ];

    const AGENTS_NAV = [
        { name: 'All Agents',    icon: 'bi-grid',         path: '/agents' },
        { name: 'Create Agent',  icon: 'bi-plus-circle',  path: '/agents/create' },
        { name: 'Tool Registry', icon: 'bi-tools',        path: '/tools' },
    ];

    const CHATBOT_NAV = [
        { name: 'Flow Builder',    icon: 'bi-diagram-3',      path: '/flows' },
        { name: 'Knowledge Base',  icon: 'bi-book',           path: '/knowledgebase' },
        { name: 'Agent Panel',     icon: 'bi-headset',        path: '/agentpanel' },
        { name: 'Agent Config',    icon: 'bi-robot',          path: '/agentconfig' },
        { name: 'Learning Review', icon: 'bi-lightbulb',      path: '/learningreview' },
        { name: 'Test Chat',       icon: 'bi-chat-dots-fill', path: '/testchat' },
    ];
    const [role, setRole] = useState(() => {
        try {
            const parsed = JSON.parse(localStorage.getItem('role') || '[]');
            return parsed.map((item) => ({ ...item, isOpen: false }));
        } catch {
            return [];
        }
    });

    // Auto-open submenu if current path is inside it
    useEffect(() => {
        setRole(prev => prev.map(item => ({
            ...item,
            isOpen: item.subModules?.some(sub => sub.path === location.pathname) || item.isOpen
        })));
    }, [location.pathname]);

    const toggleSubmenu = (index) => {
        setRole(prev => prev.map((item, i) =>
            i === index ? { ...item, isOpen: !item.isOpen } : item
        ));
    };

    const handleLogout = async () => {
        try {
            const token = localStorage.getItem('token');
            await fetch(`${url}/users/logout`, {
                method: 'POST',
                headers: { Authorization: 'Bearer ' + token },
            });
        } catch (e) {
            console.error('Logout error:', e);
        } finally {
            localStorage.removeItem('token');
            localStorage.removeItem('role');
            localStorage.removeItem('uid');
            localStorage.removeItem('balance');
            navigate('/login');
        }
    };

    const isParentActive = (item) => {
        if (item.path && location.pathname === item.path) return true;
        if (item.subModules?.some(sub => sub.path === location.pathname)) return true;
        return false;
    };

    return (
        <div style={{ ...styles.nav, width: isCollapsed ? '60px' : '240px' }}>

            {/* Brand strip + collapse button */}
            <div style={styles.navHeader}>
                {!isCollapsed && (
                    <span style={styles.navBrand}>Menu</span>
                )}
                <button
                    style={styles.toggleBtn}
                    onClick={() => setIsCollapsed(!isCollapsed)}
                    title={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                >
                    <i className={`bi ${isCollapsed ? 'bi-chevron-bar-right' : 'bi-chevron-bar-left'}`}></i>
                </button>
            </div>

            {/* Nav items */}
            <nav style={styles.navBody}>
                <ul style={styles.navList}>
                    {role?.map((item, index) => {
                        const active = isParentActive(item);
                        const isLogout = item.name === 'Logout';
                        const hasChildren = item.subModules?.length > 0;

                        return (
                            <li key={index} style={styles.navItem}>
                                {/* Parent row */}
                                <div
                                    style={{
                                        ...styles.navRow,
                                        ...(active ? styles.navRowActive : {}),
                                        ...(isLogout ? styles.navRowLogout : {}),
                                    }}
                                    title={isCollapsed ? item.name : ''}
                                    onClick={() => {
                                        if (isLogout) { handleLogout(); return; }
                                        if (hasChildren) { if (!isCollapsed) toggleSubmenu(index); }
                                        else if (item.path) navigate(item.path);
                                    }}
                                    onMouseEnter={e => {
                                        if (!active && !isLogout) e.currentTarget.style.background = '#f0f7ff';
                                        if (isLogout) e.currentTarget.style.background = '#fff1f2';
                                    }}
                                    onMouseLeave={e => {
                                        if (!active && !isLogout) e.currentTarget.style.background = 'transparent';
                                        if (isLogout) e.currentTarget.style.background = 'transparent';
                                    }}
                                >
                                    {/* Active indicator bar */}
                                    <span style={{ ...styles.activeBar, opacity: active ? 1 : 0 }}></span>

                                    {/* Icon */}
                                    <span style={{
                                        ...styles.iconWrap,
                                        background: active ? '#dbeafe' : isLogout ? '#fee2e2' : 'transparent',
                                        color: active ? '#1565c0' : isLogout ? '#dc2626' : '#6b7280',
                                    }}>
                                        <i className={item.icon || 'bi-circle'}></i>
                                    </span>

                                    {/* Label + chevron */}
                                    {!isCollapsed && (
                                        <>
                                            <span style={{
                                                ...styles.navLabel,
                                                color: active ? '#1565c0' : isLogout ? '#dc2626' : '#374151',
                                                fontWeight: active ? '600' : '500',
                                            }}>
                                                {item.name}
                                            </span>
                                            {hasChildren && (
                                                <i className={`bi bi-chevron-${item.isOpen ? 'up' : 'down'}`}
                                                    style={styles.chevron}></i>
                                            )}
                                        </>
                                    )}
                                </div>

                                {/* Submenu */}
                                {!isCollapsed && hasChildren && item.isOpen && (
                                    <ul style={styles.subList}>
                                        {item.subModules.map((child, ci) => (
                                            <li key={ci} style={{ listStyle: 'none' }}>
                                                <NavLink
                                                    to={child.path}
                                                    style={({ isActive }) => ({
                                                        ...styles.subLink,
                                                        ...(isActive ? styles.subLinkActive : {}),
                                                    })}
                                                >
                                                    {({ isActive }) => (
                                                        <>
                                                            <span style={{
                                                                ...styles.subDot,
                                                                background: isActive ? '#1565c0' : '#d1d5db',
                                                            }}></span>
                                                            <span style={{
                                                                color: isActive ? '#1565c0' : '#6b7280',
                                                                fontWeight: isActive ? '600' : '400',
                                                                fontSize: '13px',
                                                            }}>
                                                                {child.name}
                                                            </span>
                                                        </>
                                                    )}
                                                </NavLink>
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </li>
                        );
                    })}
                </ul>

                {/* ── Agents Section ──────────────────────────────────── */}
                {!isCollapsed && (
                    <div style={{ padding: '8px 14px 4px', marginTop: 8 }}>
                        <button onClick={() => setAgentsOpen(p => !p)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Agent Repo</span>
                            <i className={`bi bi-chevron-${agentsOpen ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#9ca3af' }}></i>
                        </button>
                    </div>
                )}
                {agentsOpen && (
                    <ul style={{ listStyle: 'none', padding: '4px 0', margin: 0 }}>
                        {AGENTS_NAV.map((item, index) => {
                            const active = location.pathname === item.path || (item.path === '/agents' && location.pathname.startsWith('/agents'));
                            return (
                                <li key={index} style={{ listStyle: 'none' }}>
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderLeft: `3px solid ${active ? '#1565c0' : 'transparent'}`, background: active ? '#eff6ff' : 'transparent', transition: 'all 0.15s' }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f0f7ff'; }}
                                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                        title={isCollapsed ? item.name : ''}>
                                        <span style={{ width: 28, height: 28, borderRadius: 8, background: active ? '#dbeafe' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <i className={`bi ${item.icon}`} style={{ fontSize: 14, color: active ? '#1565c0' : '#6b7280' }}></i>
                                        </span>
                                        {!isCollapsed && (
                                            <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#1565c0' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.name}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {/* ── WhatsApp Chatbot Section ─────────────────────────── */}
                {!isCollapsed && (
                    <div style={{ padding: '8px 14px 4px', marginTop: 8 }}>
                        <button onClick={() => setChatbotOpen(p => !p)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>WhatsApp Chatbot</span>
                            <i className={`bi bi-chevron-${chatbotOpen ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#9ca3af' }}></i>
                        </button>
                    </div>
                )}
                {chatbotOpen && (
                    <ul style={{ listStyle: 'none', padding: '4px 0', margin: 0 }}>
                        {CHATBOT_NAV.map((item, index) => {
                            const active = location.pathname === item.path;
                            return (
                                <li key={index} style={{ listStyle: 'none' }}>
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderLeft: `3px solid ${active ? '#1565c0' : 'transparent'}`, background: active ? '#eff6ff' : 'transparent', transition: 'all 0.15s' }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f0f7ff'; }}
                                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                        title={isCollapsed ? item.name : ''}>
                                        <span style={{ width: 28, height: 28, borderRadius: 8, background: active ? '#dbeafe' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <i className={`bi ${item.icon}`} style={{ fontSize: 14, color: active ? '#1565c0' : '#6b7280' }}></i>
                                        </span>
                                        {!isCollapsed && (
                                            <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#1565c0' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.name}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

                {/* ── Settings Section ────────────────────────────────── */}
                {!isCollapsed && (
                    <div style={{ padding: '8px 14px 4px', marginTop: 8 }}>
                        <button onClick={() => setSettingsOpen(p => !p)}
                            style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%', background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0' }}>
                            <span style={{ fontSize: 10, fontWeight: 700, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Settings</span>
                            <i className={`bi bi-chevron-${settingsOpen ? 'up' : 'down'}`} style={{ fontSize: 10, color: '#9ca3af' }}></i>
                        </button>
                    </div>
                )}
                {settingsOpen && (
                    <ul style={{ listStyle: 'none', padding: '4px 0', margin: 0 }}>
                        {SETTINGS_NAV.map((item, index) => {
                            const active = location.pathname === item.path;
                            return (
                                <li key={index} style={{ listStyle: 'none' }}>
                                    <div
                                        style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 14px', cursor: 'pointer', borderLeft: `3px solid ${active ? '#1565c0' : 'transparent'}`, background: active ? '#eff6ff' : 'transparent', transition: 'all 0.15s' }}
                                        onClick={() => navigate(item.path)}
                                        onMouseEnter={e => { if (!active) e.currentTarget.style.background = '#f0f7ff'; }}
                                        onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                                        title={isCollapsed ? item.name : ''}>
                                        <span style={{ width: 28, height: 28, borderRadius: 8, background: active ? '#dbeafe' : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                            <i className={`bi ${item.icon}`} style={{ fontSize: 14, color: active ? '#1565c0' : '#6b7280' }}></i>
                                        </span>
                                        {!isCollapsed && (
                                            <span style={{ fontSize: 13, fontWeight: active ? 600 : 500, color: active ? '#1565c0' : '#374151', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                                {item.name}
                                            </span>
                                        )}
                                    </div>
                                </li>
                            );
                        })}
                    </ul>
                )}

            </nav>

            {/* Collapsed tooltips via title attr are handled natively */}
        </div>
    );
};

const styles = {
    nav: {
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: '#ffffff',
        borderRight: '1px solid #e5e7eb',
        transition: 'width 0.25s ease',
        flexShrink: 0,
        overflowX: 'hidden',
        overflowY: 'auto',
        fontFamily: "'Segoe UI', system-ui, sans-serif",
        boxShadow: '1px 0 4px rgba(0,0,0,0.04)',
    },
    navHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        padding: '12px 10px 8px',
        borderBottom: '1px solid #f3f4f6',
        flexShrink: 0,
        minHeight: '44px',
    },
    navBrand: {
        fontSize: '11px',
        fontWeight: '700',
        color: '#9ca3af',
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
        paddingLeft: '6px',
    },
    toggleBtn: {
        background: 'none',
        border: 'none',
        color: '#9ca3af',
        cursor: 'pointer',
        padding: '4px',
        borderRadius: '6px',
        fontSize: '14px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        marginLeft: 'auto',
        flexShrink: 0,
    },
    navBody: {
        flex: 1,
        overflowY: 'auto',
        overflowX: 'hidden',
        padding: '8px 0',
    },
    navList: {
        listStyle: 'none',
        padding: '0 8px',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '2px',
    },
    navItem: { listStyle: 'none' },
    navRow: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '8px 8px 8px 6px',
        borderRadius: '8px',
        cursor: 'pointer',
        transition: 'background 0.15s',
        userSelect: 'none',
        position: 'relative',
        background: 'transparent',
    },
    navRowActive: {
        background: '#eff6ff',
    },
    navRowLogout: {
        marginTop: '4px',
    },
    activeBar: {
        position: 'absolute',
        left: 0,
        top: '20%',
        height: '60%',
        width: '3px',
        background: '#1565c0',
        borderRadius: '0 3px 3px 0',
        transition: 'opacity 0.15s',
        flexShrink: 0,
    },
    iconWrap: {
        width: '30px',
        height: '30px',
        borderRadius: '7px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: '14px',
        flexShrink: 0,
        transition: 'background 0.15s, color 0.15s',
    },
    navLabel: {
        flex: 1,
        fontSize: '13.5px',
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        transition: 'color 0.15s',
    },
    chevron: {
        fontSize: '10px',
        color: '#9ca3af',
        flexShrink: 0,
    },
    subList: {
        listStyle: 'none',
        padding: '2px 0 4px 20px',
        margin: 0,
        display: 'flex',
        flexDirection: 'column',
        gap: '1px',
    },
    subLink: {
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        padding: '7px 8px',
        borderRadius: '6px',
        textDecoration: 'none',
        transition: 'background 0.15s',
        whiteSpace: 'nowrap',
    },
    subLinkActive: {
        background: '#eff6ff',
    },
    subDot: {
        width: '6px',
        height: '6px',
        borderRadius: '50%',
        flexShrink: 0,
        transition: 'background 0.15s',
    },
};

export default LeftNavigation;
